import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadSimple, FileXls, CheckCircle, WarningCircle, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import api from "@/lib/api";

const COLLECTIONS = [
  {
    value: "tambak", label: "Tambak",
    fields: ["kode", "nama", "luas_m2", "lokasi", "blok", "wilayah", "pemilik_nama", "status"],
    required: ["kode", "nama"],
  },
  {
    value: "gudang", label: "Gudang (Item)",
    fields: ["nama", "kategori", "satuan", "stok", "harga_satuan", "stok_minimum"],
    required: ["nama", "kategori", "satuan"],
  },
  {
    value: "keuangan", label: "Keuangan",
    fields: ["tipe", "kategori", "jumlah", "tanggal", "keterangan"],
    required: ["tipe", "kategori", "jumlah", "tanggal"],
  },
  {
    value: "panen", label: "Panen",
    fields: ["siklus_id", "tambak_id", "tanggal_panen", "berat_total_kg", "size", "harga_per_kg", "pembeli", "keterangan"],
    required: ["tanggal_panen", "berat_total_kg", "size"],
  },
];

const NUM_FIELDS = ["luas_m2", "stok", "harga_satuan", "stok_minimum", "jumlah", "berat_total_kg", "size", "harga_per_kg"];

export default function ImportDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [collection, setCollection] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const selectedCol = COLLECTIONS.find((c) => c.value === collection);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (jsonData.length === 0) {
          toast.error("File kosong");
          return;
        }
        setHeaders(Object.keys(jsonData[0]));
        setPreviewData(jsonData.slice(0, 100));
      } catch {
        toast.error("Gagal membaca file Excel");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!collection || previewData.length === 0) return;
    setImporting(true);
    setResult(null);
    try {
      const cleaned = previewData.map((row) => {
        const obj = {};
        (selectedCol?.fields || []).forEach((f) => {
          if (row[f] !== undefined && row[f] !== "") {
            obj[f] = NUM_FIELDS.includes(f) ? parseFloat(row[f]) || 0 : String(row[f]);
          }
        });
        return obj;
      });
      const { data } = await api.post("/import/bulk", { collection, data: cleaned });
      setResult({ success: true, message: data.message });
      toast.success(data.message);
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal mengimpor";
      setResult({ success: false, message: msg });
      toast.error(msg);
    }
    setImporting(false);
  };

  const downloadTemplate = () => {
    if (!selectedCol) return;
    const ws = XLSX.utils.json_to_sheet([
      selectedCol.fields.reduce((acc, f) => { acc[f] = ""; return acc; }, {})
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `template_${collection}.xlsx`);
  };

  const reset = () => {
    setPreviewData([]);
    setHeaders([]);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="import-btn" variant="outline" className="border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B]/5">
          <UploadSimple size={16} className="mr-1.5" /> Import Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Work Sans" }}>Import Data dari Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          {/* Step 1: Collection */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">1. Pilih Jenis Data</Label>
            <Select value={collection} onValueChange={(v) => { setCollection(v); reset(); }}>
              <SelectTrigger data-testid="import-collection-select"><SelectValue placeholder="Pilih jenis data" /></SelectTrigger>
              <SelectContent>
                {COLLECTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Template download + field info */}
          {selectedCol && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-600">Kolom yang diperlukan:</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {selectedCol.fields.map((f) => (
                      <span key={f} className={`mr-1.5 ${selectedCol.required.includes(f) ? "font-semibold text-slate-700" : ""}`}>
                        {f}{selectedCol.required.includes(f) ? "*" : ""}
                      </span>
                    ))}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="shrink-0 text-xs">
                  <DownloadSimple size={14} className="mr-1" /> Template
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Upload file */}
          {collection && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">2. Upload File Excel</Label>
              <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg hover:border-[#B8860B] cursor-pointer transition-colors">
                <FileXls size={32} className="text-slate-400 mb-2" />
                <span className="text-sm text-slate-500">{previewData.length > 0 ? `${previewData.length} baris terdeteksi` : "Klik atau drag file .xlsx"}</span>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} data-testid="import-file-input" />
              </label>
            </div>
          )}

          {/* Preview */}
          {previewData.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">
                3. Preview ({previewData.length} baris)
              </Label>
              <div className="overflow-x-auto border rounded-lg max-h-48">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      {headers.slice(0, 8).map((h) => (
                        <TableHead key={h} className="text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap px-2 py-1.5">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 5).map((row, i) => (
                      <TableRow key={i}>
                        {headers.slice(0, 8).map((h) => (
                          <TableCell key={h} className="text-xs px-2 py-1 whitespace-nowrap max-w-[120px] truncate">{String(row[h] ?? "")}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {previewData.length > 5 && <p className="text-[10px] text-slate-400">...dan {previewData.length - 5} baris lainnya</p>}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${result.success ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {result.success ? <CheckCircle size={18} weight="bold" /> : <WarningCircle size={18} weight="bold" />}
              {result.message}
            </div>
          )}

          {/* Import button */}
          {previewData.length > 0 && !result?.success && (
            <Button
              data-testid="import-submit-btn"
              onClick={handleImport}
              disabled={importing}
              className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white"
            >
              {importing ? "Mengimpor..." : `Import ${previewData.length} Baris`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
